<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <div class="w-100 d-flex gap-3 justify-content-between align-items-center mb-5">
            <div class="mb-0">
                <h3 class="mb-0">Supplier Document Hub - Internal Management View</h3>
                <p class="mt-3 mb-0 lead text-muted">
                    Manage the Documents an resources available to your Suppliers in the Supplier Document Hub.
                </p>
                <p class="mt-2 mb-0 lead text-muted">
                    Organize things into groups based on document type, subject, or applicability.
                </p>
            </div>
            
            <div class="d-flex gap-4">
                <b-button 
                    v-if="portalDocHubUrl"
                    variant="minimal-secondary" 
                    size="lg"
                    :href="portalDocHubUrl"
                    target="_blank"
                >
                    Preview
                    <b-icon icon="box-arrow-up-right" class="ml-2"></b-icon>
                </b-button>
                <b-button
                    variant="gray-success"
                    size="lg"
                    @click="startNewGroup"
                >
                    New Group
                </b-button>
            </div>
        </div>
        
        <!-- <div class="d-flex flex-wrap"> -->
        <!-- <transition name="fade-fast" mode="out-in"> -->
            <div v-if="!initialLoadComplete || loading" key="loading" class="w-100" style="min-height: 400px; height: 400px;">
                <busy-spinner text="Loading..." class="h-100 w-100"></busy-spinner>
            </div>
            <div
                v-else 
                key="notLoading"
                class=""
            >
                <div class="row">
                    <div 
                        id="dochub-groups-content" 
                        class="col" 
                        style="height: auto; position: relative;"
                    >
                        <Draggable
                            v-model="groupsWithResources"
                            group="groups"
                            handle=".dochub-group-drag-handle"
                            draggable=".dochub-group-drag-item"
                            animation="300"
                            class="dragarea w-100"
                            :class="[(groupsWithResources.length < 1) ? 'empty-list' : '', loading ? 'opacity-60' : '' ]"
                            :disabled="isBusy"
                            @change="handleChangeDragGroup"
                            @end="handleEndDragGroup"
                            @add="handleAddDragGroup"
                            @remove="handleRemoveDragGroup"
                            @update="handleUpdateDragGroup"
                        >
                            <div 
                                v-for="group in groupsWithResources"
                                :key="group.id"
                                :id="group.uniqueId"
                                class="dochub-group-drag-item mb-4 bg-gray-200 border rounded-lg shadow-sm overflow-hidden d-flex align-items-start"
                            >
                                <div class="d-flex gap-2 flex-column align-items-center">
                                    <div class="dochub-group-drag-handle px-2 py-3 d-flex align-items-center justify-content-center">
                                        <b-icon icon="grip-vertical" variant="secondary" font-scale="3"></b-icon>
                                    </div>
                                    <div class="d-flex flex-column align-items-center">
                                        <b-button
                                            variant="minimal-secondary"
                                            size="sm"
                                            :disabled="isBusy || group.localIndex <= 0"
                                            title="Move Group Up"
                                            @click="moveGroupUp(group)"
                                        >
                                            <b-icon icon="chevron-up" font-scale="1.5"></b-icon>
                                        </b-button>
                                        <b-button
                                            variant="minimal-secondary"
                                            size="sm"
                                            :disabled="isBusy || group.localIndex >= groupsWithResources.length - 1"
                                            title="Move Group Up"
                                            @click="moveGroupDown(group)"
                                        >  <!-- tooltips on these buttons get wonky when you change their position in the array -->
                                            <b-icon icon="chevron-down" font-scale="1.5"></b-icon>
                                        </b-button>
                                    </div>
                                </div>
                                <div class="flex-fill">
                                    <!-- <div class="w-100 d-flex gap-4 justify-content-between pr-3 py-3">
                                        <h3 class="mb-0 text-primary-darker">{{ group.name }}</h3>
                                        
                                    </div> -->
                                    <div class="w-100 d-flex gap-4 align-items-center justify-content-between pr-3 py-3">
                                        <div class="w-40 text-left">
                                            <h3 class="mb-0 text-primary-darker">{{ group.name }}</h3>
                                            <p v-if="group.description" class="text-muted mb-0 mt-2">{{ group.description }}</p>
                                        </div>
                                        <div class="w-20 d-flex gap-5 align-items-start justify-content-center">
                                            <div class="">
                                                <p class="mb-1 special-text text-muted font-size-70">Layout Style</p>
                                                <p v-if="getLayoutType(group.layout_type)" class="mb-0">
                                                    <b-icon :icon="getLayoutType(group.layout_type).icon" class="text-gray-800"></b-icon>
                                                    <span class="special-text ml-1">{{ getLayoutType(group.layout_type).label }}</span>
                                                </p>
                                            </div>
                                            <!-- DO NOT BOTHER WITH NON-DEFAULT ORDERING FOR NOW, JUST MAKE IT ALL SORTABLE -->
                                            <!-- <div class="">
                                                <p class="mb-1 special-text text-muted">Ordering</p>
                                                <p v-if="getOrderingType(group.ordering_type)" class="mb-0">{{ getOrderingType(group.ordering_type).label }}</p>
                                            </div> -->
                                        </div>
                                        <div class="w-40">
                                            <div class="d-flex align-items-center justify-content-end gap-3">
                                                <b-button
                                                    variant="bare-secondary" 
                                                    class="d-flex gap-2 align-items-center justify-content-between"
                                                    @click="() => group.isExpanded = !group.isExpanded"
                                                >
                                                    <!-- v-b-toggle="`dhmg-collapse-${group.uniqueId}`" -->
                                                    <span>{{ group.isExpanded ? 'Collapse' : 'Expand' }}</span>
                                                    <b-icon :icon="group.isExpanded ? 'chevron-up' : 'chevron-down'" font-scale="1.5"></b-icon>
                                                </b-button>
                                            </div>
                                            <div class="mt-2 d-flex align-items-center justify-content-end gap-3">
                                                <b-button variant="gray-danger" @click="startDeleteGroup(group)">
                                                    <b-icon icon="trash"></b-icon>
                                                </b-button>
                                                <b-button v-if="group.canEdit" variant="gray-secondary" style="min-width: 100px;" @click="startUpdateGroup(group)">
                                                    Edit
                                                </b-button>
                                                <b-button variant="gray-success" @click="startCreateResource(group)">
                                                    + Add Item
                                                </b-button>
                                            </div>
                                        </div>
                                        
                                    </div>
                                    <b-collapse :id="`dhmg-collapse-${group.uniqueId}`" :visible="group.isExpanded">
                                        <div class="pr-3 py-3">
                                            <Draggable
                                                :list="group.resources"
                                                group="resources"
                                                handle=".dochub-resource-drag-handle"
                                                draggable=".dochub-resource-drag-item"
                                                animation="300"
                                                class="dochub-resources-dragarea dragarea w-100 list-group overflow-hidden"
                                                :class="[(group.resources.length < 1) ? 'empty-list' : 'shadow-sm', loading ? 'opacity-60' : '' ]"
                                                :disabled="isBusy || !group.canDragResources"
                                                @choose="handleChooseDragResource"
                                                @start="handleStartDragResource"
                                                @end="handleEndDragResource"
                                                @change="handleChangeDragResource"
                                                @add="handleAddDragResource"
                                                @remove="handleRemoveDragResource"
                                                @update="handleUpdateDragResource"
                                                @sort="handleSortDragResource"
                                                @filter="handleFilterDragResource"
                                                @clone="handleCloneDragResource"
                                            >
                                                <div
                                                    v-for="resource in group.resources"
                                                    :key="resource.id"
                                                    class="list-group-item p-0 h-auto dochub-resource-drag-item d-flex justify-content-between"
                                                    :class="[resource.status === 'published' ? 'bg-white' : 'bg-gray-100']"
                                                >
                                                    <div 
                                                        class="dochub-resource-drag-handle d-flex align-items-center justify-content-center"
                                                        style="padding-left: 0.75rem; padding-right: 0.75rem;"
                                                    >
                                                        <div class="status-bar d-none" :class="resource.status === 'published' ? 'published' : 'draft'"></div>
                                                        <div class="status-tag special-text" :class="resource.status === 'published' ? 'published' : 'draft'">
                                                            {{ resource.status === 'published' ? 'Live' : 'Draft' }}
                                                        </div>
                                                        <b-icon v-if="group.canDragResources" icon="grip-vertical" variant="secondary" font-scale="2"></b-icon>
                                                    </div>
                                                    <DocHubResourceDisplay
                                                        :key="resource.id"
                                                        :resource="resource"
                                                        :edit="true"
                                                        buttonSize="sm"
                                                        class="dochub-manager-item-content flex-fill"
                                                        @request-create-version="startCreateVersion"
                                                    ></DocHubResourceDisplay>
                                                    <div 
                                                        class="dochub-manager-item-content ml-3 pr-2 text-right action-area"
                                                        :class="resource.status === 'published' ? 'bg-gray-50' : 'bg-gray-150'"
                                                        style="width: 120px; max-width: 120px; min-width: 120px;"
                                                    >

                                                        <div class="d-flex flex-column align-items-end justify-content-center gap-1">    
                                                            <b-button
                                                                size="sm"
                                                                :variant="resource.status === 'published' ? 'minimal-secondary' : 'success'"
                                                                :disabled="resource.status === 'published' ? !resource.can_go_draft : !resource.can_go_published"
                                                                class="resource-action-button d-flex align-items-center justify-content-center"
                                                                @click="startChangeResourceStatus($event, resource.id, resource.status === 'published' ? 'draft' : 'published')"
                                                            >
                                                                {{ resource.status === 'published' ? 'Unpublish' : 'Publish'}} &rarr;
                                                            </b-button>
                                                            <b-button
                                                                variant="minimal-secondary"
                                                                class="resource-action-button"
                                                                size="sm"
                                                                @click="startCombinedEdit(resource)"
                                                            >
                                                                Edit &rarr;
                                                            </b-button>
                                                            <b-button
                                                                variant="minimal-danger"
                                                                class="resource-action-button"
                                                                size="sm"
                                                                @click="startDeleteResource(resource.id)"
                                                            >
                                                                Delete &rarr;
                                                            </b-button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Draggable>
                                        </div>
                                    </b-collapse>
                                </div>
                            </div>
                        </Draggable>
                    </div>
                </div>
            </div>
        <!-- </transition> -->
        <div v-if="!loading && noDocsAvailable" class="w-100 d-flex justify-content-center align-items-center" style="min-height: 400px; height: 400px;">
            <div class="font-weight-bold font-size-100 text-muted">
                No Documents
            </div>
        </div>

        <!-- GROUP FORM -->
        <b-modal
            v-model="groupEditFormShowing"
            :title="isGroupUpdate ? 'Update this group' : 'Create a new group'"
            size="md"
            hide-footer
            centered
            body-bg-variant="gray-100"
            @hidden="resetGroupEdit"
        >
            <DocHubResourceGroupForm
                :existingData="groupEditData"
                @loading-status="setFormLoadingState('group', $event)"
                @cancel="onGroupEditCancel"
                @saved="onGroupEditSaved"
            ></DocHubResourceGroupForm>
        </b-modal>

        <!-- COMBINED EDIT FORM/MODAL (resource and version) -->
        <b-modal
            v-model="combinedEditFormShowing"
            size="md"
            hide-footer
            centered
            body-bg-variant="gray-100"
            :title="resourceEditData && resourceEditData.id ? `Edit: ${resourceEditData.name}` : 'Create Document Hub Item'"
            @hidden="resetCombinedEdit"
        >
            <b-tabs
                v-model="combinedEditModalActiveTabIndex"
                no-nav-style 
                nav-class="spl-floating-nav" 
                nav-wrapper-class="w-100"
                content-class="pt-4 px-3"
            >  
                <!-- no-nav-style nav-class="spl-page-nav border-bottom" -->
                <!-- pills nav-class="spl-pill-nav" -->
                <!-- no-nav-style nav-class="spl-floating-nav" nav-wrapper-class="d-flex" -->
                <b-tab 
                    ref="combinedFormResourceTab"
                    :title="isResourceUpdate ? 'Edit Item Details' : 'Create New Item'"
                    title-item-class="flex-fill" 
                    title-link-class="text-center" 
                >
                    <DocHubResourceForm
                        v-if="resourceEditData"
                        :key="isResourceUpdate ? `resourceform_update_${resourceEditData.id}` : 'resourceform_create'"
                        :existingData="resourceEditData"
                        @loading-status="setFormLoadingState('resource', $event)"
                        @cancel="onCombinedEditCancel"
                        @updated="onCombinedEditResourceUpdated"
                        @created="onCombinedEditResourceCreated"
                    ></DocHubResourceForm>
                </b-tab>
                <b-tab 
                    ref="combinedFormVersionTab"
                    :disabled="!resourceEditData || !resourceEditData.id"
                    :title="versionEditIsFirstVersion ? 'Add Files' : 'Upload New Version'"
                    title-item-class="flex-fill" 
                    title-link-class="text-center" 
                >
                    <div v-if="!versionEditIsFirstVersion" class="mb-4">
                        <p class="mb-1 text-center">Files from the previous version are <b>not</b> copied over to the new version.</p>
                        <p class="mb-1 text-center">You must upload new copies of all files that should be visible.</p>
                    </div>
                    <DocHubResourceVersionForm
                        v-if="resourceEditData && resourceEditData.id"
                        :key="`new_version_form_${resourceEditData.id}`"
                        :resourceId="resourceEditData && resourceEditData.id"
                        :versionData="versionEditData"
                        @loading-status="setFormLoadingState('version', $event)"
                        @cancel="onCombinedEditCancel"
                        @saved="onCombinedEditVersionSaved"
                    ></DocHubResourceVersionForm>
                </b-tab>
            </b-tabs>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
import { errorHandling, currentUserInfoMixin, splUrlMapMixin } from '../../js/custom-vue-mixins';
import { FormatDateHelper } from '@/js/filters-helpers';
import Draggable from 'vuedraggable';
//import DocHubVersionText from './DocHubVersionText.vue';
import DocHubResourceForm from './DocHubResourceForm.vue';
import DocHubResourceVersionForm from './DocHubResourceVersionForm.vue';
import DocHubResourceGroupForm from './DocHubResourceGroupForm.vue';
//import DocHubCloudflareVideoButton from './DocHubCloudflareVideoButton.vue';
import DocHubResourceDisplay from './DocHubResourceDisplay.vue';
import { LAYOUT_TYPES_BY_VALUE, ORDERING_TYPES_BY_VALUE, NO_GROUP_DATA, fetchGroupsWithResources } from './dochubHelpers';
//import BaseS3FileDownloadButtonGroup from '@/components/s3_files/BaseS3FileDownloadButtonGroup.vue';
import cloneDeep from 'lodash/cloneDeep';
//import orderBy from 'lodash/orderBy';


export default {
    name: 'DocHubManager',
    components: {
        Draggable,
        //DocHubVersionText,
        DocHubResourceForm,
        DocHubResourceVersionForm,
        DocHubResourceGroupForm,
        //DocHubCloudflareVideoButton,
        DocHubResourceDisplay,
        //BaseS3FileDownloadButtonGroup,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
        splUrlMapMixin,
    ],
    props: {
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            updating: false,
            initialLoadComplete: false,
            // RESOURCES
            resourcesById: {},  // map of resource_id to resource for ALL resources, not inside groups
            groupsWithResources: [],  // list of DocHubResourceGroups from API, with resources inside, including "no group" group, if still supported
            // EDIT FORMS
            isResourceUpdate: false,  // change from different methods
            resourceEditData: null, // set it depending on update/create -- also set it when doing a new version
            versionEditData: {}, // set it depending on update/create  (we don't allow updates for versions for now, so always will be set to {})
            combinedEditFormShowing: false,
            combinedEditModalActiveTabIndex: 0,  // 0 for resource, 1 for version
            formLoadingStates: {
                group: false,
                resource: false,
                version: false,
            },
            // GROUPS
            groupEditFormShowing: false,
            isGroupUpdate: false,  // change from different methods
            groupEditData: null, // set it depending on update/create
            loadingEnumOptions: false,
            layoutOptions: [],
            orderingOptions: [],
            // OTHER
            fileDownloadButtonProps: {
                buttonVariant: 'subtle-primary',
                size: 'sm',
                block: true,
                hideFilesize: true,
            },
        }
    },
    computed: {
        portalDocHubUrl() {
            return this.$splUrlMap['portal_dochub'] || '';
        },
        noDocsAvailable() {
            return Object.values(this.resourcesById).length < 1;
        },
        versionEditIsFirstVersion() {
            // do we already have a version?  or are we adding the first one?
            return !this.resourceEditData?.active_version?.id;  
        },
        anyFormsLoading() {
            return Object.values(this.formLoadingStates).some(v => v);
        },
        isBusy() {
            return this.loading || this.loadingEnumOptions || this.updating || this.anyFormsLoading;
        },
    },
    async mounted() {
        return Promise.all([
            this.getResourcesAndGroups(),
            this.getEnumOptions(),
        ]);
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async refresh() {
            await this.getResourcesAndGroups();
            // not reloading enumOptions, as they should not change
        },
        async getResourcesAndGroups() {
            this.loading = true;
            //let resp;
            try {
                const groupsWithResources = await fetchGroupsWithResources({forManager: true, onlyPublished: false});
                // now add some extra stuff to each group that is only used here in manager
                groupsWithResources.forEach(g => {
                    g.isExpanded = true;  // used to control the collapse
                });
                this.groupsWithResources = groupsWithResources;
                // now have to set this.resourcesById
                const allResources = [];
                this.groupsWithResources.forEach(g => {
                    allResources.push(...g.resources);
                });
                this.resourcesById = Object.fromEntries(allResources.map(r => [r.id, r]));
                this.initialLoadComplete = true;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        async getEnumOptions() {
            this.loadingEnumOptions = true;
            let resp;
            try {
                resp = await suppliosApi.dochubGroupManage.enum_options();
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (resp?.data) {
                this.layoutOptions = Array.isArray(resp.data.layout_type) ? resp.data.layout_type : [];  // {value, label}
                this.orderingOptions = Array.isArray(resp.data.ordering_type) ? resp.data.ordering_type : [];  // {value, label}
            }
            this.loadingEnumOptions = false;
        },
        getLayoutType(layoutTypeValue) {
            return LAYOUT_TYPES_BY_VALUE[layoutTypeValue];
        },
        getOrderingType(orderingTypeValue) {
            return ORDERING_TYPES_BY_VALUE[orderingTypeValue];
        },
        async startChangeResourceStatus(event, resourceId, newStatus) {
            console.log('startChangeResourceStatus', event, resourceId, newStatus);
            const resource = this.resourcesById[resourceId];
            if (!resource) {
                console.log('no resource found for ID', resourceId, 'resourcesById:', this.resourcesById);
                return;
            }
            const toPublishMsg = `Are you sure you want to publish '${resource.name}'?  This will make it visible to all Suppliers and Internal Users.`;
            const toDraftMsg = `Are you sure you want to unpublish '${resource.name}'?  All Suppliers and Internal Users will no longer have access to it.`;
            const willGoPublished = newStatus === 'published';
            const confirmMsg = willGoPublished ? toPublishMsg : toDraftMsg;
            const confirmTitle = willGoPublished ? `Publish ${resource.name}?` : `Unpublish ${resource.name}?`;
            this.$bvModal.msgBoxConfirm(confirmMsg, {
                title: confirmTitle,
                size: 'sm',
                buttonSize: 'sm',
                okVariant: willGoPublished ? 'success' : 'primary',
                okTitle: willGoPublished ? 'Yes, Publish' : 'Yes, Proceed',
                cancelTitle: 'No, Cancel',
                cancelVariant: 'secondary',
                footerClass: 'p-2 border-top-0',
                centered: true,
            })
            .then(proceed => {
                if (proceed) {
                    this.performChangeResourceStatus(resourceId, newStatus);
                }
            })
            .catch(err => {
                // An error occurred
                console.error(`error while trying to change resource status to ${newStatus} for resource ID ${resourceId}`, err);
            });
        },
        async performChangeResourceStatus(resourceId, newStatus) {
            console.log('performChangeResourceStatus', resourceId, newStatus);
            this.loading = true;
            const postData = {status: newStatus};
            try {
                const resp = await suppliosApi.dochubResource.change_status(resourceId, postData);
                console.log('updated status', resp.data);
                this.$toast('Updated Status');
                this.refresh();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.refresh();  // for now just refresh all, otherwise have to update store
            this.loading = false;
        },
        async startDeleteResource(resourceId) {
            console.log('startDeleteResource', resourceId);
            const resourceObject = this.resourcesById[resourceId];
            if (!resourceObject) {
                return;
            }
            this.$bvModal.msgBoxConfirm(`Are you sure you want to delete '${resourceObject.name}'?  All current and previous versions and any attached files will also be deleted.  This cannot be undone.`, {
                title: 'Delete Item?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
                cancelVariant: 'secondary',
                footerClass: 'p-2 border-top-0',
                centered: true,
            })
            .then(proceed => {
                if (proceed) {
                    this.performDeleteResource(resourceId);
                }
            })
            .catch(err => {
                // An error occurred
                console.error('error while trying to delete resource', err);
            });
        },
        async performDeleteResource(resourceId) {
            console.log('deleteResource', resourceId);
            this.loading = true;
            try {
                const resp = await suppliosApi.dochubResource.destroy(resourceId);
                console.log('deleted resource', resp.data);
                this.$toast('Successfully deleted item');
                this.refresh();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loading = false;
        },
        setFormLoadingState(formName, isLoading) {
            this.$set(this.formLoadingStates, formName, isLoading);
        },
        // GROUPS
        async startDeleteGroup(groupObj) {
            // check if we can, if we can, show modal to confirm
            console.log('startDeleteGroup', groupObj);
            if (!groupObj) {
                return;
            }
            if (groupObj.resources.length > 0) {  // still has resources... tell user it can't be done
                return this.$bvModal.msgBoxOk(`You must remove all items from this group before it can be deleted.`, {
                    title: 'Not Allowed',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'secondary',
                    footerClass: 'p-2 border-top-0',
                    centered: true,
                });
            } else {
                return this.$bvModal.msgBoxConfirm(`Are you sure you want to delete '${groupObj.name}'?`, {
                    title: 'Delete Group?',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes, Delete',
                    cancelTitle: 'No, Cancel',
                    cancelVariant: 'secondary',
                    footerClass: 'p-2 border-top-0',
                    centered: true,
                })
                .then(proceed => {
                    if (proceed) {
                        return this.performDeleteGroup(groupObj.id);
                    }
                })
                .catch(err => {
                    // An error occurred
                    console.error('error while trying to delete resource', err);
                });
            }
        },
        async performDeleteGroup(groupId) {
            console.log('performDeleteGroup', groupId);
            this.loading = true;
            try {
                const resp = await suppliosApi.dochubGroupManage.destroy(groupId);
                console.log('deleted group', resp.data);
                this.$toast('Successfully deleted group');
                this.refresh();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.loading = false;
        },
        startNewGroup() {
            this.groupEditData = {
                sort_order: this.groupsWithResources.length,  // set sort_order to the next available index
            };
            this.isGroupUpdate = false;
            this.groupEditFormShowing = true;
        },
        startUpdateGroup(groupData) {
            this.groupEditData = cloneDeep(groupData);
            this.isGroupUpdate = true;
            this.groupEditFormShowing = true;
        },
        onGroupEditCancel() {
            this.resetGroupEdit();
            // no need to refresh from parent here
        },
        onGroupEditSaved() {
            this.resetGroupEdit();
            this.refresh();  // ???
        },
        resetGroupEdit() {
            this.groupEditFormShowing = false;
            this.isGroupUpdate = false;
            this.groupEditData = null;
        },
        // COMBINED EDIT FORM - Resource and Version
        startCreateResource(group) {
            const groupId = group.id;
            console.log('startCreateResource for groupId', groupId);
            // changed to now require groupId for all resources
            if (!groupId) {
                console.error('Must specify the group when adding a resource - no groupId provided');
                return;
            }
            // set sort_order also, based on count in the group
            // must make sure your resource form doesn't pull it off
            const nextIndex = group.resources.length;
            const resourceEditData = {
                group: groupId,
                sort_order: nextIndex,
            };
            // let realGroupId = null;  // default...
            // if (!groupId) {
            //     realGroupId = null;  // always default to null
            // } else {
            //     realGroupId = groupId === NO_GROUP_DATA.id ? null : groupId;
            // }
            // const resourceEditData = {group: realGroupId};
            // just delegate to the combined edit form now
            this.startCombinedEdit(resourceEditData);
        },
        startCreateVersion(resourceId) {
            // start a NEW version only -- go direct to the version form after opening combined modal
            this.combinedEditModalActiveTabIndex = 1; // switch to version tab first, so it opens on that
            const resourceEditData = this.resourcesById[resourceId];
            this.startCombinedEdit(resourceEditData);  // let startCombinedEdit handle the rest
        },
        startCombinedEdit(resourceData) { 
            // can use this for both updating an existing resource and creating a new one! 
            // for creates, we pass in resourceData but only the group is used (or populated), no 'id' so it will be a create
            const resourceId = resourceData.id || '';
            console.log('startCombinedEdit', resourceData);
            if (!resourceId) {
                // CREATE new resource since no ID - version tab will be disabled
                console.log('startCombinedEdit - CREATE A RESOURCE');
                this.resourceEditData = {...resourceData};
                this.isResourceUpdate = false;  // its a create instead
            } else {
                // UPDATE existing resource - even though we passed in the full object, we'll re-find it from our local store for consistency?
                console.log('startCombinedEdit - UPDATE A RESOURCE');
                this.resourceEditData = cloneDeep(resourceData); // this.resourcesById[resourceId];
                this.isResourceUpdate = true;  // its an update
            }
            this.versionEditData = {};  // always blank here, we don't allow editing versions directly
            this.combinedEditFormShowing = true;  // finally open the modal
        },
        resetCombinedEdit() {
            // RESET ALL THE THINGS
            this.combinedEditFormShowing = false;
            this.combinedEditModalActiveTabIndex = 0;  // back to resource, first tab
            this.isResourceUpdate = false;
            this.resourceEditData = null;  // reset to empty object?  would need to default to that too for consistency
            this.versionEditData = {};
        },
        onCombinedEditCancel() {
            this.resetCombinedEdit();
            // no need to refresh from parent here
        },
        onCombinedEditResourceCreated(newResourceData) {
            // we just CREATED a new resource, not a new version (yet)
            // keep modal open, populate resourceEditData with new resource, switch to version tabs
            this.resourceEditData = {...newResourceData};
            this.isResourceUpdate = true;  // switches the resource form to update mode (its keyed, so it will re-render)
            this.versionEditData = {};
            this.$nextTick(() => {
                // this.$refs.combinedFormVersionTab.activate();  // switch the tab -- forms will get re-rendered
                this.combinedEditModalActiveTabIndex = 1;  // switch the tab -- forms will get re-rendered because of setting new stuff above
            });
        },
        onCombinedEditResourceUpdated() {
            // we just UPDATED (existing) resource details, not a new version
            this.resetCombinedEdit();
            this.refresh();
        },
        onCombinedEditVersionSaved() {
            // SAVED a new version, not a new resource (should we check that we didn't have any unsaved data in the resource form?)
            this.resetCombinedEdit();
            this.refresh();  // ???
        },
        // HELPERS
        formatDate(dateString) {
            return FormatDateHelper(dateString);
        },
        // SORTING STUFF!
        async moveGroupDown(groupObj) {
            // move a group DOWN in the list, meaning INCREASE it's sort_order/localIndex
            console.log('moveGroupDown', groupObj);
            const currentIndex = groupObj.localIndex;
            if (currentIndex >= this.groupsWithResources.length - 1) {
                return;  // if we were already at bottom, do nothing
            }
            // if good, then splice the array to move it down, then call updateGroupSortOrder to capture the new order and send to API
            // we will remove the group, and the NEXT group, then re-insert them in the correct order
            const nextGroupObj = this.groupsWithResources[currentIndex + 1];
            this.groupsWithResources.splice(currentIndex, 2, nextGroupObj, groupObj);  // start removing at currentIndex, remove 2, then insert those 2 in swapped order
            console.log('new group order', this.groupsWithResources.map(g => g.name));
            await this.updateGroupSortOrder();
        },
        async moveGroupUp(groupObj) {
            // move a group UP in the list, meaning DECREASE it's sort_order/localIndex
            console.log('moveGroupUp', groupObj);
            const currentIndex = groupObj.localIndex;
            if (currentIndex < 1) {
                return;  // if we were already at top, do nothing
            }
            // if good, then splice the array to move it down, then call updateGroupSortOrder to capture the new order and send to API
            // we will remove the group, and the PREVIOUS group, then re-insert them in the correct order
            const prevIndex = currentIndex - 1;
            const prevGroupObj = this.groupsWithResources[prevIndex];
            this.groupsWithResources.splice(prevIndex, 2, groupObj, prevGroupObj);  // start removing at currentIndex, remove 2, then insert those 2 in swapped order
            console.log('new group order', this.groupsWithResources.map(g => g.name));
            await this.updateGroupSortOrder();
        },
        async updateGroupSortOrder() {
            // send to API to update the sort order of the GROUPS - called from draggable event
            // we just wait until the end of the drag event to update the sort order... 
            // they are now in the correct order, just grab 'snapshot' and save
            // also must reset the localIndex for each group
            const indexByIds = {};
            this.groupsWithResources.forEach((g, newIndex) => {
                indexByIds[g.id] = newIndex;
                this.$set(g, 'localIndex', newIndex);  // must do this since we do NOT do a refresh of data
            });
            this.updating = true;
            try {
                console.log('START will now update group sort order via API', indexByIds);
                const resp = await suppliosApi.dochubGroupManage.update_group_sort_order(indexByIds);
                console.log('COMPLETE updated group sort order via API', resp.data);
                // this.$toast('Updated Group Sort Order');
                // this.refresh();  // should not need to refresh since we only updated things in place
            } catch (error) {
                this.apiErrorHandler(error);
            }
            console.log('groupsWithResources after update', this.groupsWithResources);
            this.updating = false;
        },
        async updateResourceSortOrder() {
            // send to API to update the sort order of the RESOURCES - called from draggable event
            // need to update the sort order AND the group_id for each resource in case they moved groups
            // we just wait until the end of the drag event to do it
            // they are now in the correct order, just grab 'snapshot' and save
            // api returns the new sort_order and group_id for each resource (even if not changed)
            const newResourceDataById = {};
            this.groupsWithResources.forEach(g => {
                const isNoGroup = g.id === NO_GROUP_DATA.id;
                g.resources.forEach((r, newIndex) => {
                    newResourceDataById[r.id] = {sort_order: newIndex, group: (isNoGroup ? null : g.id)};
                });
            });
            this.updating = true;
            try {
                console.log('START will now update RESOURCE sorting via API', newResourceDataById);
                const resp = await suppliosApi.dochubGroupManage.update_resource_sorting(newResourceDataById);
                console.log('COMPLETE updated RESOURCE sorting via API', resp.data);
                // now go through all returned resources and update the local data - data is object {resourceId: {sort_order, group}}
                // should you do it here?  or in local data before API call?
                if (resp.data) {
                    Object.entries(resp.data).forEach(([resourceId, newResourceData]) => {
                        const resource = this.resourcesById[resourceId];
                        if (resource) {
                            this.$set(resource, 'sort_order', newResourceData.sort_order);
                            this.$set(resource, 'group', newResourceData.group);
                        }
                    });
                }
            } catch (error) {
                this.apiErrorHandler(error);
            }
            // TODO - need to update the local data with the new sort order and group_id, or do a full refresh!
            this.updating = false;
        },
        // RESOURCE DRAGGING
        handleChooseDragResource(evt) {
            console.log('handleChooseDragResource', evt);
        },
        handleStartDragResource(evt) {
            console.log('handleStartDragResource', evt);
        },
        handleEndDragResource(evt) {
            // triggers even if no change in order
            console.log('handleEndDragResource', evt);
            this.updateResourceSortOrder();  // TODO - make this check if there were actually any changes
            // need to hook in here or else you'll have multiple events firing when changing groups which is confusing
        },
        handleChangeDragResource(evt) {
            // this fires when the list order actually changes... trigger from this for order updates
            console.log('handleChangeDragResource', evt);
            
        },
        handleAddDragResource(evt) {
            console.log('handleAddDragResource', evt);
        },
        handleRemoveDragResource(evt) {
            console.log('handleRemoveDragResource', evt);
        },
        handleUpdateDragResource(evt) {
            console.log('handleUpdateDragResource', evt);
            
        },
        handleSortDragResource(evt) {
            console.log('handleSortDragResource', evt);
        },
        handleFilterDragResource(evt) {
            console.log('handleFilterDragResource', evt);
        },
        handleCloneDragResource(evt) {
            console.log('handleCloneDragResource', evt);
        },
        // GROUP DRAGGING
        handleChangeDragGroup(evt) {
            console.log('handleChangeDragGroup', evt);
        },
        handleAddDragGroup(evt) {
            console.log('handleAddDragGroup', evt);
        },
        handleRemoveDragGroup(evt) {
            console.log('handleRemoveDragGroup', evt);
        },
        handleUpdateDragGroup(evt) {
            console.log('handleUpdateDragGroup', evt);
            
        },
        handleEndDragGroup(evt) {
            console.log('handleEndDragGroup', evt);
            this.updateGroupSortOrder();
        },
    }
}
</script>

<style lang="scss">

.dochub-group-collapse-button.collapsed .dochub-group-collapse-icons > .when-not-collapsed,
.dochub-group-collapse-button.not-collapsed .dochub-group-collapse-icons > .when-collapsed {
    display: none;
}

.dochub-manager-item-content {
    padding-top: 12px;
    padding-bottom: 12px;
}

.dochub-group-drag-handle,
.dochub-resource-drag-handle {
    cursor: move;
}

.dochub-resource-drag-handle {
    position: relative;
    .status-tag {
        position: absolute;
        top: 0;
        left: 0;
        font-size: 10px;
        font-weight: 700;
        //text-transform: uppercase;
        padding: 3px 8px 3px 7px;
        //border-width: 1px 0 1px 1px;
        //border-style: solid;
        border-top-right-radius: 0;
        border-bottom-right-radius: 5px;
        //color: #fff;
        &.published {
            background-color: var(--success-100);
            color: var(--success-700);
            //border-color: var(--success-700);
        }
        &.draft {
            background-color: var(--gray-300);
            color: var(--gray-700);
            //border-color: var(--gray-700);
        }
    }
    .status-bar {
        position: absolute;
        top: 0;
        left: 0;
        width: 8px;
        height: 100%;
        &.published {
            background-color: var(--success-100);
        }
        &.draft {
            background-color: var(--gray-300);
        }
    }
}

.resource-action-link {
    //special-text font-size-70 text-decoration-none
    text-transform: uppercase;
    font-size: 80%;
    font-weight: 500;
    text-decoration: none;
    opacity: 0.9;
    transition: all 0.2s;
    &:hover {
        opacity: 1;
        text-decoration: none;
    }
}

.resource-action-button {
    //font-size: 80%;
    padding-top: 2px;
    padding-bottom: 2px;
    font-weight: 500;
    text-transform: uppercase;
    // .button-icon {
    //     font-size: 120%;
    // }
}

.dragarea.dochub-resources-dragarea {
    &.empty-list {
        min-height: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 120%;
        color: var(--gray-400);
        background-color: var(--gray-300);
        border: 1px dashed var(--gray-500);

        &::after {
            content: '+ Add Items or Drag Items Here';
        }
    }
}

</style>